import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { concatMap, tap } from "rxjs/operators";
import { LoginRequest, LoginResponse } from "../types/auth.type";
import { environment } from "../../../environments/environment";
import { Role, RolesResponse, User } from "../types/user.type";
import { StorageService } from "./storage.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  private isLoggedInSubject = new BehaviorSubject<boolean>(this.isLoggedIn());
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();

  constructor(private http: HttpClient, private storage: StorageService) {}

  login(credentials: LoginRequest): Observable<User> {
    return this.http
      .post<LoginResponse>(`${this.apiUrl}/auth/login`, credentials)
      .pipe(
        tap((response) => {
          this.storage.setItem("accessToken", response.accessToken);
          this.storage.setItem("refreshToken", response.refreshToken);
          this.storage.removeItem("roles");
          this.storage.removeItem("company");
          this.storage.removeItem("user");
          this.storage.removeItem("areas");
        }),
        concatMap(() => this.getUserInfo()),
        tap(() => this.isLoggedInSubject.next(true))
      );
  }

  forgotPassword(email: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/auth/forgot-password`, {
      email,
    });
  }

  validateCode(code: string, email: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/auth/validate-reset-code`, {
      code,
      email,
    });
  }

  resetPassword(
    password: string,
    code: string,
    email: string
  ): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/auth/reset-password`, {
      password,
      code,
      email,
    });
  }

  getUserInfo(): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/user`).pipe(
      tap((response) => {
        this.storage.setItem("roles", JSON.stringify(response.roles));
        this.storage.setItem("company", JSON.stringify(response.company));
        this.storage.setItem("areas", JSON.stringify(response.areas));
        this.storage.setItem(
          "user",
          JSON.stringify({
            id: response.id,
            name: response.name,
            email: response.email,
            position: response.position,
          })
        );
        this.updateCSSVariables(
          response.company.configuration.primaryColor,
          response.company.configuration.secondaryColor
        );
        this.updateFavicon(response.company.configuration.logoUrl);
      })
    );
  }

  updateCSSVariables(
    primaryColor?: string | null,
    secondaryColor?: string | null
  ) {
    if (primaryColor)
      document.documentElement.style.setProperty(
        "--primary-color",
        primaryColor
      );
    if (secondaryColor)
      document.documentElement.style.setProperty(
        "--secondary-color",
        secondaryColor
      );
  }

  updateFavicon(faviconUrl?: string | null) {
    if (!faviconUrl) {
      return;
    }
    const linkElement = document.getElementById("favicon") as HTMLLinkElement;
    if (linkElement) {
      linkElement.href = faviconUrl;
    }
  }

  logout(): void {
    this.storage.removeItem("accessToken");
    this.storage.removeItem("refreshToken");
    this.isLoggedInSubject.next(false);
  }

  isLoggedIn(): boolean {
    return (
      !!this.storage?.getItem("accessToken") && !!this.storage?.getItem("user")
    );
  }

  getUserRole(): string[] {
    return (
      JSON.parse(this.storage?.getItem("roles") ?? "{}")?.map(
        (role: Role) => role.name
      ) ?? ""
    );
  }

  getToken(): string | null {
    return this.storage?.getItem("accessToken");
  }

  getRoles(): Observable<RolesResponse[]> {
    return this.http.get<RolesResponse[]>(`${this.apiUrl}/auth/roles`, {});
  }
}
